import { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { LogOut, Inbox, FileText, MessageSquare, Trash2, Check, Plus, Eye, X, Edit2, ExternalLink } from "lucide-react";
import { toast, Toaster } from "sonner";

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("submissions");

  if (loading) return <div className="min-h-screen flex items-center justify-center text-brand-muted">Loading...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-brand-surface">
      <Toaster position="top-right" />
      <header className="bg-white border-b border-brand-border sticky top-0 z-30">
        <div className="container-x flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <Logo />
            <span className="text-xs px-2 py-0.5 bg-brand-ink text-white rounded-full font-medium">Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="admin-view-site" className="text-sm text-brand-muted hover:text-brand-ink flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5" /> View Site</Link>
            <button onClick={logout} data-testid="admin-logout" className="text-sm text-brand-muted hover:text-brand-ink flex items-center gap-1"><LogOut className="w-3.5 h-3.5" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="container-x py-8">
        <div className="flex items-center gap-2 mb-6 overflow-x-auto">
          <TabBtn active={tab === "submissions"} onClick={() => setTab("submissions")} testid="admin-tab-submissions" icon={Inbox}>Contact Submissions</TabBtn>
          <TabBtn active={tab === "blog"} onClick={() => setTab("blog")} testid="admin-tab-blog" icon={FileText}>Blog Posts</TabBtn>
          <TabBtn active={tab === "chat"} onClick={() => setTab("chat")} testid="admin-tab-chat" icon={MessageSquare}>Live Chat</TabBtn>
        </div>

        {tab === "submissions" && <SubmissionsPanel />}
        {tab === "blog" && <BlogPanel />}
        {tab === "chat" && <ChatPanel />}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, icon: Icon, testid }) {
  return (
    <button onClick={onClick} data-testid={testid} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${active ? "bg-brand-ink text-white" : "bg-white border border-brand-border text-brand-ink hover:border-brand-ink"}`}>
      <Icon className="w-4 h-4" /> {children}
    </button>
  );
}

function SubmissionsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/contact-submissions").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    await api.patch(`/admin/contact-submissions/${id}/read`);
    setItems((arr) => arr.map((x) => (x.id === id ? { ...x, is_read: true } : x)));
    toast.success("Marked as read");
  };

  const del = async (id) => {
    if (!confirm("Delete this submission?")) return;
    await api.delete(`/admin/contact-submissions/${id}`);
    setItems((arr) => arr.filter((x) => x.id !== id));
    if (selected?.id === id) setSelected(null);
    toast.success("Deleted");
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
      <div className="lg:col-span-5">
        <div className="bg-white border border-brand-border rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-brand-border flex items-center justify-between">
            <div className="font-medium text-brand-ink">All Submissions</div>
            <div className="text-xs text-brand-muted" data-testid="admin-submissions-count">{items.length} total</div>
          </div>
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-brand-border">
            {loading ? <div className="p-6 text-center text-brand-muted text-sm">Loading...</div> :
              items.length === 0 ? <div className="p-6 text-center text-brand-muted text-sm">No submissions yet.</div> :
                items.map((it) => (
                  <button key={it.id} onClick={() => setSelected(it)} data-testid={`admin-submission-item-${it.id}`}
                    className={`w-full text-left px-5 py-4 hover:bg-brand-surface transition-colors ${selected?.id === it.id ? "bg-brand-surface" : ""}`}>
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="font-medium text-brand-ink text-sm">{it.name}</div>
                      {!it.is_read && <span className="w-2 h-2 rounded-full bg-brand-blue mt-1.5" />}
                    </div>
                    <div className="text-xs text-brand-muted truncate">{it.email}</div>
                    <div className="text-xs text-brand-muted mt-1 line-clamp-2">{it.message}</div>
                    <div className="text-[10px] text-brand-muted mt-1.5">{new Date(it.created_at).toLocaleString()}</div>
                  </button>
                ))}
          </div>
        </div>
      </div>

      <div className="lg:col-span-7">
        {selected ? (
          <div className="bg-white border border-brand-border rounded-2xl p-6" data-testid="admin-submission-detail">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h2 className="font-display font-bold text-2xl text-brand-ink">{selected.name}</h2>
                <a href={`mailto:${selected.email}`} className="text-brand-blue text-sm">{selected.email}</a>
              </div>
              <div className="flex items-center gap-2">
                {!selected.is_read && (
                  <button onClick={() => markRead(selected.id)} data-testid="admin-submission-mark-read" className="text-sm inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-brand-border hover:border-brand-ink"><Check className="w-3.5 h-3.5" /> Mark read</button>
                )}
                <button onClick={() => del(selected.id)} data-testid="admin-submission-delete" className="text-sm inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6 text-sm">
              <Detail label="Phone" value={selected.phone} />
              <Detail label="Company" value={selected.company} />
              <Detail label="Service" value={selected.service} />
              <Detail label="Budget" value={selected.budget} />
              <Detail label="Received" value={new Date(selected.created_at).toLocaleString()} />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-brand-muted mb-2">Message</div>
              <div className="p-4 bg-brand-surface rounded-xl text-brand-ink whitespace-pre-wrap leading-relaxed">{selected.message}</div>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-brand-border rounded-2xl p-12 text-center text-brand-muted">
            <Eye className="w-8 h-8 mx-auto mb-3 text-brand-muted" />
            Select a submission to view details
          </div>
        )}
      </div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-widest text-brand-muted">{label}</div>
      <div className="text-brand-ink mt-1">{value || "—"}</div>
    </div>
  );
}

function BlogPanel() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/blog").then((r) => setPosts(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!confirm("Delete this post?")) return;
    await api.delete(`/admin/blog/${id}`);
    setPosts((arr) => arr.filter((p) => p.id !== id));
    toast.success("Post deleted");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div className="text-sm text-brand-muted" data-testid="admin-blog-count">{posts.length} post{posts.length !== 1 ? "s" : ""}</div>
        <button onClick={() => setEditing({})} data-testid="admin-blog-new" className="btn-primary text-sm py-2.5 px-4"><Plus className="w-4 h-4" /> New Post</button>
      </div>

      <div className="bg-white border border-brand-border rounded-2xl overflow-hidden">
        {loading ? <div className="p-8 text-center text-brand-muted text-sm">Loading...</div> :
          posts.length === 0 ? <div className="p-12 text-center text-brand-muted text-sm">No posts yet. Create your first post.</div> :
            <div className="divide-y divide-brand-border">
              {posts.map((p) => (
                <div key={p.id} data-testid={`admin-blog-row-${p.id}`} className="p-5 flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="font-medium text-brand-ink">{p.title}</div>
                      {p.published ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-800">Published</span> : <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-700">Draft</span>}
                    </div>
                    <div className="text-xs text-brand-muted line-clamp-1">/blog/{p.slug}</div>
                    <div className="text-xs text-brand-muted mt-1">{new Date(p.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => setEditing(p)} data-testid={`admin-blog-edit-${p.id}`} className="p-2 rounded-lg border border-brand-border hover:border-brand-ink"><Edit2 className="w-3.5 h-3.5" /></button>
                    <button onClick={() => del(p.id)} data-testid={`admin-blog-delete-${p.id}`} className="p-2 rounded-lg border border-red-200 text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
              ))}
            </div>}
      </div>

      {editing && <BlogEditor post={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function BlogEditor({ post, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: post.title || "",
    excerpt: post.excerpt || "",
    content: post.content || "",
    cover_image: post.cover_image || "",
    author: post.author || "TelstraNics Team",
    tags: (post.tags || []).join(", "),
    published: post.published ?? true,
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      if (post.id) await api.put(`/admin/blog/${post.id}`, payload);
      else await api.post("/admin/blog", payload);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/50 flex items-center justify-center p-4" data-testid="admin-blog-editor">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-brand-border px-6 py-4 flex items-center justify-between">
          <div className="font-display font-bold text-lg">{post.id ? "Edit Post" : "New Post"}</div>
          <button onClick={onClose} data-testid="admin-blog-editor-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <F label="Title *"><input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="admin-blog-title" className="inp" /></F>
          <F label="Excerpt *"><textarea required value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} data-testid="admin-blog-excerpt" className="inp min-h-[70px]" /></F>
          <F label="Cover image URL"><input value={form.cover_image} onChange={(e) => setForm({ ...form, cover_image: e.target.value })} data-testid="admin-blog-cover" className="inp" placeholder="https://..." /></F>
          <div className="grid grid-cols-2 gap-4">
            <F label="Author"><input value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} data-testid="admin-blog-author" className="inp" /></F>
            <F label="Tags (comma-separated)"><input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="admin-blog-tags" className="inp" placeholder="Design, Growth" /></F>
          </div>
          <F label="Content * (paragraphs separated by blank line, use ## for h2)"><textarea required value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="admin-blog-content" className="inp min-h-[280px] font-mono text-sm" /></F>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="admin-blog-published" />
            Published (visible on the public blog)
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-outline text-sm py-2.5 px-4">Cancel</button>
            <button type="submit" disabled={saving} data-testid="admin-blog-save" className="btn-primary text-sm py-2.5 px-4">{saving ? "Saving..." : "Save"}</button>
          </div>
          <style>{`.inp{width:100%;padding:0.6rem 0.85rem;border:1px solid #E5E7EB;border-radius:0.65rem;font-size:0.9rem;background:#fff}.inp:focus{outline:none;border-color:#0A0A0A;box-shadow:0 0 0 3px rgba(37,99,235,.1)}`}</style>
        </form>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return <label className="block"><span className="text-sm font-medium text-brand-ink mb-1.5 block">{label}</span>{children}</label>;
}

function ChatPanel() {
  const [sessions, setSessions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get("/admin/chat/sessions").then((r) => setSessions(r.data)).finally(() => setLoading(false));
  }, []);

  const openSession = (s) => {
    setSel(s);
    api.get(`/admin/chat/${s.session_id}`).then((r) => setMessages(r.data));
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
      <div className="lg:col-span-5">
        <div className="bg-white border border-brand-border rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-brand-border font-medium">Chat Sessions</div>
          <div className="max-h-[70vh] overflow-y-auto divide-y divide-brand-border">
            {loading ? <div className="p-6 text-center text-brand-muted text-sm">Loading...</div> :
              sessions.length === 0 ? <div className="p-8 text-center text-brand-muted text-sm">No chat sessions yet.</div> :
                sessions.map((s) => (
                  <button key={s.session_id} onClick={() => openSession(s)} data-testid={`admin-chat-session-${s.session_id}`}
                    className={`w-full text-left px-5 py-4 hover:bg-brand-surface ${sel?.session_id === s.session_id ? "bg-brand-surface" : ""}`}>
                    <div className="flex items-start justify-between">
                      <div className="font-medium text-brand-ink text-sm">{s.name || "Anonymous"}</div>
                      <div className="text-xs text-brand-muted">{s.count} msg</div>
                    </div>
                    <div className="text-xs text-brand-muted">{s.email || "—"}</div>
                    <div className="text-xs text-brand-muted mt-1 line-clamp-1">{s.last_message}</div>
                  </button>
                ))}
          </div>
        </div>
      </div>

      <div className="lg:col-span-7">
        {sel ? (
          <div className="bg-white border border-brand-border rounded-2xl p-6" data-testid="admin-chat-detail">
            <div className="font-display font-bold text-lg mb-4">{sel.name || "Anonymous"} <span className="text-sm font-normal text-brand-muted">{sel.email}</span></div>
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {messages.map((m) => (
                <div key={m.id} className="p-3 bg-brand-surface rounded-xl text-sm">
                  <div className="text-brand-ink">{m.message}</div>
                  <div className="text-xs text-brand-muted mt-1">{new Date(m.created_at).toLocaleString()}</div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white border border-brand-border rounded-2xl p-12 text-center text-brand-muted">Select a chat to view messages</div>
        )}
      </div>
    </div>
  );
}
